import { Modal, Tabs, Table, Badge, Text, Stack, Group, ScrollArea, Loader, Center } from '@mantine/core'
import { IconListSearch, IconGavel } from '@tabler/icons-react'
import { useState } from 'react'
import type { SearchDashboardData, CaseData, ComplianceDashboardData } from '@/types'
import { useCasesBySearchId, useOffensesBySearchId } from '@/hooks/useAnalytics'
import { OffenseDetailDialog } from './OffenseDetailDialog'

interface SearchDetailDialogProps {
  opened: boolean
  onClose: () => void
  search: SearchDashboardData | null
}

export function SearchDetailDialog({ opened, onClose, search }: SearchDetailDialogProps) {
  const [activeTab, setActiveTab] = useState<string | null>('cases')
  const [selectedOffense, setSelectedOffense] = useState<ComplianceDashboardData | null>(null)
  const [offenseDialogOpen, setOffenseDialogOpen] = useState(false)
  const [caseFilter, setCaseFilter] = useState<string | null>(null)

  const casesQuery = useCasesBySearchId(opened && search ? search.search_id : null)
  const offensesQuery = useOffensesBySearchId(opened && search ? search.search_id : null)

  if (!search) return null

  const extractArray = (queryData: any): any[] => {
    if (!queryData) return []
    if (Array.isArray(queryData)) return queryData
    if (queryData.data) {
      if (Array.isArray(queryData.data)) return queryData.data
      if (queryData.data.rows && Array.isArray(queryData.data.rows)) return queryData.data.rows
    }
    if (queryData.rows && Array.isArray(queryData.rows)) return queryData.rows
    return []
  }

  const cases: CaseData[] = extractArray(casesQuery.data)
  const allOffenses: ComplianceDashboardData[] = extractArray(offensesQuery.data)
  const offenses = caseFilter
    ? allOffenses.filter(o => o.case_number === caseFilter)
    : allOffenses

  const handleOffenseClick = (offense: ComplianceDashboardData) => {
    setSelectedOffense(offense)
    setOffenseDialogOpen(true)
  }

  const handleCaseClick = (caseData: CaseData) => {
    setCaseFilter(caseData.case_number || null)
    setActiveTab('offenses')
  }

  return (
    <>
      <Modal
        opened={opened}
        onClose={onClose}
        title={
          <Group gap="xs">
            <Text fw={600} size="lg">Search Detail</Text>
            <Badge variant="light" size="lg">{search.search_id}</Badge>
            {search.search_type && (
              <Badge variant="outline" color="blue" size="sm">{search.search_type}</Badge>
            )}
          </Group>
        }
        size="80%"
        centered
        styles={{ body: { minHeight: 400 } }}
      >
        <Stack gap="md">
          <Group gap="lg">
            <Text size="sm" c="dimmed">
              Total Offenses: <Text span fw={600} c="dark">{search.total_offenses}</Text>
            </Text>
            <Text size="sm" c="dimmed">
              Reportable: <Text span fw={600} c="green">{search.reportable_count}</Text>
            </Text>
            <Text size="sm" c="dimmed">
              Not Reportable: <Text span fw={600} c="orange">{search.not_reportable_count}</Text>
            </Text>
            <Text size="sm" c="dimmed">
              Identity: <Text span fw={600} c="cyan">{search.identity_level || '-'}</Text>
            </Text>
          </Group>

          <Tabs value={activeTab} onChange={setActiveTab}>
            <Tabs.List>
              <Tabs.Tab value="cases" leftSection={<IconListSearch size={16} />}>
                Cases ({cases.length})
              </Tabs.Tab>
              <Tabs.Tab value="offenses" leftSection={<IconGavel size={16} />}>
                Offenses ({offenses.length}{caseFilter ? ` for ${caseFilter}` : ''})
              </Tabs.Tab>
            </Tabs.List>

            <Tabs.Panel value="cases" pt="md">
              {casesQuery.isLoading ? (
                <Center py="xl"><Loader /></Center>
              ) : casesQuery.isError ? (
                <Text c="red" ta="center" py="xl">Error loading cases: {(casesQuery.error as Error)?.message}</Text>
              ) : cases.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">No cases found for this search.</Text>
              ) : (
                <ScrollArea h={400}>
                  <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Court Search ID</Table.Th>
                        <Table.Th>Record ID</Table.Th>
                        <Table.Th>Case Number</Table.Th>
                        <Table.Th>Jurisdiction</Table.Th>
                        <Table.Th>State</Table.Th>
                        <Table.Th>Offenses</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {cases.map((c, i) => (
                        <Table.Tr
                          key={c.record_id || c.court_search_id || i}
                          style={{ cursor: c.offense_count > 0 ? 'pointer' : undefined }}
                          onClick={() => c.offense_count > 0 && handleCaseClick(c)}
                        >
                          <Table.Td><Text size="sm" ff="monospace">{c.court_search_id || '-'}</Text></Table.Td>
                          <Table.Td><Text size="sm" ff="monospace">{c.record_id}</Text></Table.Td>
                          <Table.Td><Text size="sm" c="blue" fw={500}>{c.case_number || '-'}</Text></Table.Td>
                          <Table.Td>{c.jurisdiction || '-'}</Table.Td>
                          <Table.Td>{c.jurisdiction_state || '-'}</Table.Td>
                          <Table.Td>
                            <Badge variant="filled" size="xs" color={c.offense_count > 0 ? 'blue' : 'gray'}>
                              {c.offense_count} →
                            </Badge>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Tabs.Panel>

            <Tabs.Panel value="offenses" pt="md">
              {caseFilter && (
                <Group gap="xs" mb="sm">
                  <Badge variant="light" color="blue" size="lg">
                    Filtered by case: {caseFilter}
                  </Badge>
                  <Text
                    size="sm"
                    c="blue"
                    style={{ cursor: 'pointer', textDecoration: 'underline' }}
                    onClick={() => setCaseFilter(null)}
                  >
                    Show all offenses
                  </Text>
                </Group>
              )}
              {offensesQuery.isLoading ? (
                <Center py="xl"><Loader /></Center>
              ) : offensesQuery.isError ? (
                <Text c="red" ta="center" py="xl">Error loading offenses: {(offensesQuery.error as Error)?.message}</Text>
              ) : offenses.length === 0 ? (
                <Text c="dimmed" ta="center" py="xl">
                  {caseFilter ? `No offenses found for case ${caseFilter}.` : 'No offenses found for this search.'}
                </Text>
              ) : (
                <ScrollArea h={400}>
                  <Table striped highlightOnHover withTableBorder withColumnBorders>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>Offense ID</Table.Th>
                        <Table.Th>Case Number</Table.Th>
                        <Table.Th>Charge</Table.Th>
                        <Table.Th>Disposition</Table.Th>
                        <Table.Th>Ground Truth</Table.Th>
                        <Table.Th>AI Decision</Table.Th>
                        <Table.Th>Identity Match</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {offenses.map((o, i) => (
                          <Table.Tr
                            key={o.offense_id || i}
                            style={{ cursor: 'pointer' }}
                            onClick={() => handleOffenseClick(o)}
                          >
                            <Table.Td><Text size="sm" ff="monospace">{o.offense_id}</Text></Table.Td>
                            <Table.Td><Text size="sm">{o.case_number || '-'}</Text></Table.Td>
                            <Table.Td><Text size="sm" lineClamp={1}>{o.charge || '-'}</Text></Table.Td>
                            <Table.Td><Text size="sm" lineClamp={1}>{o.disposition || '-'}</Text></Table.Td>
                            <Table.Td>
                              {o.ground_truth ? (
                                <Badge
                                  variant="light"
                                  color={o.ground_truth === 'REPORTABLE' ? 'red' : 'green'}
                                  size="xs"
                                >
                                  {o.ground_truth}
                                </Badge>
                              ) : '-'}
                            </Table.Td>
                            <Table.Td>
                              {o.ce_decision ? (
                                <Badge
                                  variant="light"
                                  color={o.ce_decision === 'REPORTABLE' ? 'red' : 'green'}
                                  size="xs"
                                >
                                  {o.ce_decision}
                                </Badge>
                              ) : '-'}
                            </Table.Td>
                            <Table.Td>
                              {(o as any).id_match_validated != null ? (
                                <Badge variant="light" color="cyan" size="xs">
                                  {String((o as any).id_match_validated)}
                                </Badge>
                              ) : <Text size="sm" c="dimmed">-</Text>}
                            </Table.Td>
                          </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </ScrollArea>
              )}
            </Tabs.Panel>
          </Tabs>
        </Stack>
      </Modal>

      <OffenseDetailDialog
        opened={offenseDialogOpen}
        onClose={() => setOffenseDialogOpen(false)}
        offense={selectedOffense}
        allOffensesInSearch={allOffenses}
      />
    </>
  )
}
